import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router'
import { ApolloError } from 'apollo-client'
import { useKey, useSessionStorage } from 'react-use'

// Constants
import { IS_EMBEDDED } from 'constants/embedding'
import { routes } from 'constants/routes'

// Components
import { IsEmbedded, NotEmbedded } from 'components/Embedding'
import { ZoomTypes } from 'components/Zoom'
import { CenterContent } from 'containers/CenterContent'
import { useToast } from 'containers/ToastManager'
import DocumentSidebarLayout from 'containers/DocumentSidebarLayout'
import DetailHeader from 'modules/shares/components/DetailHeader'
import { EmbeddedDetailHeader } from 'modules/shares/components/EmbeddedDetailHeader'
import ArtboardView from 'modules/shares/components/ArtboardView'
import { ProjectsMenu } from 'modules/projects/components/ProjectsMenu'
import { CustomFontsMessage } from 'modules/shares/components/CustomFontsMessage'

// Local Components
import DetailSidebar, {
  getTabOptionFromString,
  TabOptions,
} from '../components/DetailSidebar'
import { EmbeddedErrorPage } from '../components/EmbeddedErrorPage'

// Styles
import { PageContent, StyledBackButton } from './DetailView.styles'

// Types
import { LayerId, Pagination } from '../types'
import {
  ArtboardDetailInfoFragment,
  ShareInfoFragment,
  VersionFragment,
} from 'gqlTypes'

// Hooks
import useGetInspectorData from './useGetInspectorData'
import { useSymbol } from '../Inspector/Symbol'
import { useRevisionSubscriptions } from 'modules/activity/operations/useRevisionSubscriptions'
import { useShareWorkspaceSidebar } from 'modules/shares/hooks/useShareWorkspaceSidebar'

// Operations
import { RevisionChangedProps } from 'modules/activity/operations'

// Utils
import {
  showWorkspaceSettings,
  canUserAdministerShares,
} from 'modules/workspace/utils'
import { useFlag } from 'constants/flags'

/**
 * TYPES
 */
export interface DetailViewProps {
  share: OmitSafe<ShareInfoFragment, 'version'>
  currentVersion: VersionFragment | undefined
  artboard: ArtboardDetailInfoFragment | undefined
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  pagination: Pagination | undefined
  artboardIdAtLatestVersion: string | undefined
  error: ApolloError | undefined
  loading: boolean
  onRevisionChange: (props: RevisionChangedProps) => void
}

/**
 * COMPONENT
 */
export const DetailView = ({
  share,
  currentVersion,
  artboard,
  isViewingLatestVersion,
  isPrototypePlayEnabled,
  pagination,
  artboardIdAtLatestVersion,
  onRevisionChange,
  error,
  loading,
}: DetailViewProps) => {
  const webCanvasMvpFF = useFlag('web-canvas-mvp')
  // Internal state
  const [hoveredLayerId, setHoveredLayerId] = useState<LayerId | undefined>()
  const [selectedLayerId, setSelectedLayerId] = useState<LayerId | undefined>()
  const [zoom, setZoom] = useSessionStorage<ZoomTypes>('artboard-zoom', {
    type: 'ZOOM_TO_FIT',
  })
  const [inspectorActive, setInspectorActive] = useSessionStorage<boolean>(
    'inspector-active',
    false
  )
  const [selectedTab, setSelectedTab] = useState(
    getTabOptionFromString(
      inspectorActive ? TabOptions.INSPECTOR : TabOptions.ACTIVITY,
      // ignore the leading '#' character in the window location hash
      window ? window.location.hash.slice(1) : ''
    )
  )

  // Manage inspector data
  const artboardId = artboard?.identifier
  const [
    loadInspectorData,
    { inspectorData, inspectorError, inspectorErrorReason },
  ] = useGetInspectorData({
    artboard,
  })

  // Reset selected layers when the artboard change
  useEffect(() => {
    setSelectedLayerId(undefined)
    setHoveredLayerId(undefined)
  }, [artboardId])

  // Symbol data needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const {
    symbolLayer,
    symbolData,
    symbolArtboard,
    foreignSymbolShareId,
    foreignVersionShortId,
  } = useSymbol({
    inspectorData,
    selectedLayerId,
    currentVersion,
  })

  // Load inspector data if the inspector tab is selected
  useEffect(() => {
    if (
      !artboard ||
      // if the selected tab is inspector, we want to load the inspector data
      (!inspectorActive && selectedTab !== TabOptions.INSPECTOR) ||
      !artboardId
    ) {
      return
    }

    loadInspectorData()
    setInspectorActive(true)
  }, [
    artboard,
    loadInspectorData,
    inspectorActive,
    artboardId,
    selectedTab,
    setInspectorActive,
  ])

  // Inspector data object needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const inspector = {
    selectedLayerId,
    hoveredLayerId,
    data: inspectorData,
    isActive: inspectorActive,
    onClickLayer: setSelectedLayerId,
    onHoverLayer: setHoveredLayerId,
    symbol: {
      symbolLayer,
      symbolData,
      symbolArtboard: symbolArtboard
        ? {
            ...symbolArtboard,
            // TODO: mark id fields of Artboard as non nullable
            // https://github.com/sketch-hq/Cloud/issues/4679
            permanentArtboardShortId: symbolArtboard.permanentArtboardShortId!,
          }
        : null,
      foreignSymbolShareId,
      foreignVersionShortId,
    },
  }

  // Trigger the artboards list refetch when there are new artboard revisions,
  // to be able to show the `See latest update` badge
  useRevisionSubscriptions({
    share: {
      identifier: share.identifier,
      userCanAccessVersions: share.userCanAccessVersions,
    },
    artboard: {
      // TODO: mark some Artboard fields as non nullable
      // https://github.com/sketch-hq/Cloud/issues/4679
      identifier: artboard?.identifier!,
      permanentArtboardShortId: artboard?.permanentArtboardShortId!,
    },
    onRevisionChange,
  })

  // keyboard shortcuts
  useKey(
    'Escape',
    () => {
      if (inspectorActive && selectedLayerId) {
        setSelectedLayerId(undefined)
      }
    },
    {},
    [selectedLayerId]
  )

  const { showToast } = useToast()
  useEffect(() => {
    if (error && !IS_EMBEDDED) {
      showToast('This artboard does not exist.', 'negative')
    }
  }, [error, showToast])

  const {
    shouldRenderWorkspaceSidebar,
    workspace,
    workspaces,
  } = useShareWorkspaceSidebar(share)

  const userCanInspect =
    share.userCanInspect || share.publicAccessLevel === 'INSPECT'

  const shouldRenderSidebarRight =
    !error && !IS_EMBEDDED && !!artboard && !!share && !!currentVersion

  return (
    <CenterContent>
      <DocumentSidebarLayout
        title={artboard?.name}
        header={({
          toggleSidebarRight,
          isSidebarRightOpen,
          isSidebarLeftOpen,
          setSidebarLeftOpen,
        }) => (
          <>
            <NotEmbedded>
              <DetailHeader
                currentZoom={zoom}
                onZoomChanged={setZoom}
                artboard={artboard!}
                isViewingLatestVersion={isViewingLatestVersion}
                isPrototypePlayEnabled={isPrototypePlayEnabled}
                artboardIdAtLatestVersion={artboardIdAtLatestVersion!}
                pagination={pagination}
                toggleActivity={toggleSidebarRight}
                activityIsOpen={isSidebarRightOpen}
                share={share}
                isSidebarLeftOpen={isSidebarLeftOpen}
                setSidebarLeftOpen={setSidebarLeftOpen}
                userCanOpenInApp={
                  currentVersion?.document?.userCanOpenInApp ?? false
                }
              />
            </NotEmbedded>
            <IsEmbedded>
              {!error && !loading && (
                <EmbeddedDetailHeader
                  artboardName={artboard ? artboard.name : ''}
                  pagination={pagination}
                  currentZoom={zoom}
                  onZoomChanged={setZoom}
                  showPrototypePlayButton={
                    (artboard &&
                      artboard.belongsToPrototype &&
                      isPrototypePlayEnabled) ||
                    false
                  }
                />
              )}
            </IsEmbedded>
          </>
        )}
        sidebarLeft={
          shouldRenderWorkspaceSidebar ? (
            <ProjectsMenu
              workspace={workspace!}
              workspaces={workspaces!}
              showWorkspaceSettings={showWorkspaceSettings(workspace)}
              canUserAdministerShares={canUserAdministerShares(workspace)}
            />
          ) : undefined
        }
        sidebarRight={
          shouldRenderSidebarRight ? (
            <DetailSidebar
              artboard={{
                ...artboard!,
                // TODO: mark id fields of Artboard as non nullable
                // https://github.com/sketch-hq/Cloud/issues/4679
                permanentArtboardShortId: artboard!.permanentArtboardShortId!,
                documentVersionShortId: artboard!.documentVersionShortId!,
                revisionIdentifier: artboard!.revisionIdentifier!,
              }}
              inspector={inspector}
              userCanInspect={userCanInspect}
              inspectorReady={!!inspectorData}
              inspectorError={inspectorError}
              inspectorErrorReason={inspectorErrorReason}
              selectedLayer={
                selectedLayerId &&
                inspectorData &&
                inspectorData.layers[selectedLayerId as string]
              }
              share={{
                ...share,
                // TODO: mark main fields of Share as non nullable
                // https://github.com/sketch-hq/Cloud/issues/4683
                identifier: share.identifier!,
                commentsEnabled: share.commentsEnabled!,
              }}
              currentVersion={currentVersion!}
              selectedTab={selectedTab}
              onTabSelected={selectedTab => {
                setSelectedTab(selectedTab)
                setInspectorActive(selectedTab === TabOptions.INSPECTOR)
              }}
            />
          ) : undefined
        }>
        <PageContent>
          {
            // <DismissibleCard /> will render a warning when there are
            // missing fonts in the artboard. If dismissed it wont render
            // again for any artboard in that particular document
          }
          <CustomFontsMessage
            userCanContribute={share.userCanContribute}
            shareIdentifier={share.identifier}
            hasMissingFonts={!!artboard?.hasMissingFonts}
          />
          {error ? (
            <>
              <NotEmbedded>
                <Redirect
                  to={routes.SHARE_VIEW.create({ shareID: share.identifier })}
                />
              </NotEmbedded>
              <IsEmbedded>
                <EmbeddedErrorPage for="artboard" />
              </IsEmbedded>
            </>
          ) : (
            <ArtboardView
              artboard={artboard}
              currentZoom={zoom}
              onZoomChanged={setZoom}
              inspector={inspector}
              shareIdentifier={share.identifier}
              permanentArtboardShortId={
                artboard?.permanentArtboardShortId ?? undefined
              }
            />
          )}
          {webCanvasMvpFF && <StyledBackButton />}
        </PageContent>
      </DocumentSidebarLayout>
    </CenterContent>
  )
}
